import { EngagementManagingSourceModel } from 'er';
import { useCallback, useMemo } from 'react';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import ukgLogo from 'src/static/images/ukg-avatar-small.svg';
import utmostLogo from 'src/static/images/utmost/logo-small.svg';
import workdayLogo from 'src/static/images/workday-logo-small.svg';
import { useTenantConfig } from '../useTenantConfig';

const MANAGING_SOURCES: { uid: EngagementManagingSourceModel; name: string; icon: string }[] = [
  { name: 'Beeline Professional', uid: 'UTMOST', icon: utmostLogo },
  { name: 'Workday®', uid: 'WORKDAY', icon: workdayLogo },
  { name: 'UKG®', uid: 'UKG', icon: ukgLogo }
];

export const useManagingSources = () => {
  const tenantConfig = useTenantConfig();

  const { customManagingSourceIcons } = useDarkly();

  const overrides = useMemo(
    () => ({
      UTMOST: {
        label: tenantConfig?.features?.appearance?.config?.UTMOST_LABEL,
        icon: tenantConfig?.features?.appearance?.config?.UTMOST_ICON
      },
      WORKDAY: {
        label: tenantConfig?.features?.appearance?.config?.WORKDAY_LABEL,
        icon: tenantConfig?.features?.appearance?.config?.WORKDAY_ICON
      },
      UKG: {
        label: tenantConfig?.features?.appearance?.config?.UKG_LABEL,
        icon: tenantConfig?.features?.appearance?.config?.UKG_ICON
      }
    }),
    [tenantConfig?.features?.appearance?.config]
  );

  const calculateLabel = useCallback(
    (uid: any, name: any) => {
      if (customManagingSourceIcons) {
        return (overrides[uid as keyof typeof overrides]?.label?.length || 0) > 0
          ? (overrides[uid as keyof typeof overrides]?.label ?? name)
          : name;
      }
      return name;
    },
    [customManagingSourceIcons, overrides]
  );

  const calculateIcon = useCallback(
    (uid: any, icon: any) => {
      if (customManagingSourceIcons) {
        return (overrides[uid as keyof typeof overrides]?.icon?.length || 0) > 0
          ? (overrides[uid as keyof typeof overrides]?.icon ?? icon)
          : icon;
      }
      return icon;
    },
    [customManagingSourceIcons, overrides]
  );

  const isEnabled = useCallback(
    (uid: any) => {
      if (uid === 'UKG') {
        return tenantConfig?.tenantConfig?.ukgConfig?.enabled === true;
      } else {
        return true;
      }
    },
    [tenantConfig]
  );

  const managingSourcesOptions: { label: string; value: EngagementManagingSourceModel; icon: string }[] = useMemo(
    () =>
      MANAGING_SOURCES.map(({ name, uid, icon }) => ({
        value: uid,
        label: calculateLabel(uid, name),
        icon: calculateIcon(uid, icon)
      })).filter((item) => isEnabled(item.value)),
    [calculateIcon, calculateLabel, isEnabled]
  );

  return {
    loading: false,
    managingSources: MANAGING_SOURCES.filter((item) => isEnabled(item.uid)),
    managingSourcesOptions
  };
};
